import { entityEdgesWithoutSubset, moveToFulfilled, sortEntityEdges, update } from '@ms/yammer-data-support';
import type { Reducer } from 'redux';

import type { FeedCountsLoadGroupFulfilledAction } from '../../domains/feedCounts/actions.types';
import type {
  FeedLoadFulfilledAction,
  FeedLoadNewerThreadsFulfilledAction,
  FeedLoadOlderThreadsFulfilledAction,
} from '../../domains/feedLoad/actions.types';
import type { FeedMarkAllGroupMessagesAsSeenFulfilledAction } from '../../domains/feeds/actions.types';
import { inboxUnreadFeedId } from '../../domains/feeds/getId';
import type { InboxUnreadCountLoadAction } from '../../domains/inboxUnreadCountLoad/actions.types';
import type {
  MyGroupsLoadFulfilledAction,
  MyGroupsLoadMoreFulfilledAction,
} from '../../domains/myGroups/actions.types';
import type { MyLeadersUnseenCountLoadFulfilledAction } from '../../domains/myLeadersUnseenCountLoad/actions.types';
import type { ThreadMarkAsSeenFulfilledAction } from '../../domains/threadMarkAsGroupSeenOnServer/actions.types';
import type {
  ThreadMarkAsInboxReadAction,
  ThreadMarkAsInboxUnreadAction,
  ThreadMarkAsInboxUnreadFulfilledAction,
  ThreadMarkAsInboxUnreadRealtimeAction,
} from '../../domains/threadMarkAsRead/actions.types';
import type { UnseenThreadsLoadFulfilledAction } from '../../domains/unseenThreadsLoad/actions.types';
import type { Feed, Thread, ThreadEdge } from '../types';

import type { UnseenThreadEdges, UnseenThreadEdgesForFeed, UnseenThreadStarterEdgesForFeed } from './types';

const initialState: UnseenThreadEdges = {
  pending: { byId: {} },
  fulfilled: { byId: {} },
  rejected: { byId: {} },
};

export type UnseenThreadEdgesAction =
  | InboxUnreadCountLoadAction
  | ThreadMarkAsInboxReadAction
  | ThreadMarkAsInboxUnreadAction
  | FeedCountsLoadGroupFulfilledAction
  | FeedLoadFulfilledAction
  | FeedLoadOlderThreadsFulfilledAction
  | FeedLoadNewerThreadsFulfilledAction
  | ThreadMarkAsSeenFulfilledAction
  | UnseenThreadsLoadFulfilledAction
  | FeedMarkAllGroupMessagesAsSeenFulfilledAction
  | MyGroupsLoadFulfilledAction
  | MyGroupsLoadMoreFulfilledAction
  | MyLeadersUnseenCountLoadFulfilledAction;

const removeThreadEdges = (state: UnseenThreadEdges, feedId: Feed['id'], threadEdges: ThreadEdge[]) => {
  const unseenThreadsForFeed = state.fulfilled.byId[feedId];
  if (!unseenThreadsForFeed) {
    return state;
  }

  const updatedThreadEdges = entityEdgesWithoutSubset<Thread>(unseenThreadsForFeed.threadEdges, threadEdges);
  if (updatedThreadEdges.length === unseenThreadsForFeed.threadEdges.length) {
    return state;
  }

  return update(state, feedId, {
    ...unseenThreadsForFeed,
    threadEdges: updatedThreadEdges,
  });
};

const addThreadEdges = (state: UnseenThreadEdges, feedId: Feed['id'], threadEdges: ThreadEdge[]) => {
  const unseenThreadsForFeed = state.fulfilled.byId[feedId];
  if (!unseenThreadsForFeed) {
    return moveToFulfilled(state, {
      [feedId]: {
        id: feedId,
        threadEdges,
        hasOlderThreads: false,
      },
    });
  }

  const edgesNotAlreadyStored = entityEdgesWithoutSubset<Thread>(threadEdges, unseenThreadsForFeed.threadEdges);

  if (edgesNotAlreadyStored.length === 0) {
    return state;
  }

  const updatedThreadEdges = [...unseenThreadsForFeed.threadEdges, ...edgesNotAlreadyStored];

  return update(state, feedId, {
    ...unseenThreadsForFeed,
    threadEdges: sortEntityEdges(updatedThreadEdges),
  });
};

type UpdateThreadEdgesForFeed = (
  state: UnseenThreadEdges,
  threadEdgesForFeeds?: Record<string, UnseenThreadEdgesForFeed>
) => UnseenThreadEdges;

const updateThreadEdgesForFeed: UpdateThreadEdgesForFeed = (state, threadEdgesForFeeds) => {
  if (!threadEdgesForFeeds) {
    return state;
  }

  return Object.values(threadEdgesForFeeds).reduce(
    (updatedState, currentEdges) =>
      updateThreadEdges(updatedState, currentEdges.id, { [currentEdges.id]: currentEdges }),
    state
  );
};

type UpdateThreadEdges = (
  state: UnseenThreadEdges,
  feedId: Feed['id'],
  threadEdgesForFeeds: Record<string, UnseenThreadEdgesForFeed>
) => UnseenThreadEdges;

const updateThreadEdges: UpdateThreadEdges = (state, feedId, threadEdgesForFeeds) => {
  const unseenThreadsForFeed = state.fulfilled.byId[feedId];

  if (!unseenThreadsForFeed) {
    return moveToFulfilled(state, threadEdgesForFeeds);
  }

  return update(state, feedId, {
    threadEdges: threadEdgesForFeeds[feedId].threadEdges,
    hasOlderThreads: threadEdgesForFeeds[feedId].hasOlderThreads,
  });
};

type UpdateThreadStarterEdgesForFeed = (
  state: UnseenThreadEdges,
  threadStarterEdgesForFeeds?: Record<string, UnseenThreadStarterEdgesForFeed>
) => UnseenThreadEdges;

const updateThreadStarterEdgesForFeed: UpdateThreadStarterEdgesForFeed = (state, threadStarterEdgesForFeeds) => {
  if (!threadStarterEdgesForFeeds) {
    return state;
  }

  return Object.values(threadStarterEdgesForFeeds).reduce((updatedState, currentEdges) => {
    const existing = updatedState.fulfilled.byId[currentEdges.id];

    if (existing) {
      return update(updatedState, currentEdges.id, {
        ...existing,
        threadStarterEdges: currentEdges.threadStarterEdges,
        hasOlderThreadStarters: currentEdges.hasOlderThreadStarters,
      });
    }

    return moveToFulfilled(updatedState, {
      [currentEdges.id]: {
        id: currentEdges.id,
        threadEdges: [],
        hasOlderThreads: false,
        threadStarterEdges: currentEdges.threadStarterEdges,
        hasOlderThreadStarters: currentEdges.hasOlderThreadStarters,
      },
    });
  }, state);
};

type UpdateThreadEdgesOnInboxCountLoad = (
  state: UnseenThreadEdges,
  unseenThreadEdges: UnseenThreadEdgesForFeed[]
) => UnseenThreadEdges;

const updateThreadEdgesOnInboxCountLoad: UpdateThreadEdgesOnInboxCountLoad = (state, unseenThreadEdges) =>
  unseenThreadEdges.reduce(
    (updatedState, currentEdges) =>
      updateThreadEdges(updatedState, currentEdges.id, { [currentEdges.id]: currentEdges }),
    state
  );

type UpdateThreadEdgesOnMarkAsInboxUnread = (
  state: UnseenThreadEdges,
  action: ThreadMarkAsInboxUnreadFulfilledAction | ThreadMarkAsInboxUnreadRealtimeAction
) => UnseenThreadEdges;

const updateThreadEdgesOnMarkAsInboxUnread: UpdateThreadEdgesOnMarkAsInboxUnread = (state, action) => {
  const unreadFeedThreadEdges = action.payload.unreadFeedThreadEdges || [];
  const threadEdges = [action.payload.threadEdge, ...unreadFeedThreadEdges];

  return addThreadEdges(state, inboxUnreadFeedId, threadEdges);
};

type UpdateThreadEdgesOnMarkSeen = (
  state: UnseenThreadEdges,
  action: ThreadMarkAsSeenFulfilledAction
) => UnseenThreadEdges;

const updateThreadEdgesOnMarkSeen: UpdateThreadEdgesOnMarkSeen = (state, action) => {
  const feedIds = Object.keys(action.payload.threadEdgesToRemoveByFeedId);

  return feedIds.reduce((newState, feedId) => {
    const threadEdges = action.payload.threadEdgesToRemoveByFeedId[feedId];

    return removeThreadEdges(newState, feedId, threadEdges);
  }, state);
};

const removeAllThreadEdges = (state: UnseenThreadEdges, feedId: Feed['id']) => {
  const unseenThreadsForFeed = state.fulfilled.byId[feedId];
  if (!unseenThreadsForFeed) {
    return state;
  }

  const updatedUnseenThreadEdges = {
    ...unseenThreadsForFeed,
    threadEdges: [],
    hasOlderThreads: false,
  };

  return update(state, feedId, updatedUnseenThreadEdges);
};

const reducer: Reducer<UnseenThreadEdges, UnseenThreadEdgesAction> = (state = initialState, action) => {
  switch (action.type) {
    case 'INBOX_UNREAD_COUNT.LOAD_FULFILLED':
      return updateThreadEdgesOnInboxCountLoad(state, action.payload.unseenThreadEdges);
    case 'UNSEEN_THREADS.LOAD_FULFILLED':
      return updateThreadStarterEdgesForFeed(
        moveToFulfilled(state, action.payload.entities.unseenThreadEdges),
        action.payload.entities.unseenThreadStarterEdges
      );
    case 'FEED_COUNTS.LOAD_GROUP_FULFILLED':
      return updateThreadStarterEdgesForFeed(
        updateThreadEdgesForFeed(state, action.payload.entities.unseenThreadEdges),
        action.payload.entities.unseenThreadStarterEdges
      );
    case 'FEED.LOAD_FULFILLED':
      return updateThreadStarterEdgesForFeed(
        updateThreadEdgesForFeed(state, action.payload.entities.unseenThreadEdges),
        action.payload.entities.unseenThreadStarterEdges
      );
    case 'FEED.LOAD_OLDER_THREADS_FULFILLED':
      return updateThreadStarterEdgesForFeed(
        updateThreadEdgesForFeed(state, action.payload.entities.unseenThreadEdges),
        action.payload.entities.unseenThreadStarterEdges
      );
    case 'FEED.LOAD_NEWER_THREADS_FULFILLED':
      return updateThreadStarterEdgesForFeed(
        updateThreadEdgesForFeed(state, action.payload.entities.unseenThreadEdges),
        action.payload.entities.unseenThreadStarterEdges
      );
    case 'THREAD.MARK_AS_INBOX_READ_FULFILLED':
      return removeThreadEdges(state, inboxUnreadFeedId, [action.payload.threadEdge]);
    case 'THREAD.MARK_AS_INBOX_READ_REALTIME':
      return removeThreadEdges(state, inboxUnreadFeedId, [action.payload.threadEdge]);
    case 'THREAD.MARK_AS_INBOX_UNREAD_FULFILLED':
      return updateThreadEdgesOnMarkAsInboxUnread(state, action);
    case 'THREAD.MARK_AS_INBOX_UNREAD_REALTIME':
      return updateThreadEdgesOnMarkAsInboxUnread(state, action);
    case 'THREAD.MARK_AS_GROUP_SEEN_FULFILLED':
      return updateThreadEdgesOnMarkSeen(state, action);
    case 'THREAD.MARK_ALL_AS_INBOX_READ_REALTIME':
      return removeAllThreadEdges(state, inboxUnreadFeedId);
    case 'FEED.MARK_ALL_GROUP_MESSAGES_AS_SEEN_FULFILLED':
      return removeAllThreadEdges(state, action.payload.feedId);
    case 'MY_GROUPS.LOAD_FULFILLED':
      return updateThreadStarterEdgesForFeed(
        moveToFulfilled(state, action.payload.entities.unseenThreads.unseenThreadEdges),
        action.payload.entities.unseenThreads.unseenThreadStarterEdges
      );
    case 'MY_GROUPS.LOAD_MORE_FULFILLED':
      return updateThreadStarterEdgesForFeed(
        moveToFulfilled(state, action.payload.entities.unseenThreads.unseenThreadEdges),
        action.payload.entities.unseenThreads.unseenThreadStarterEdges
      );
    case 'MY_LEADERS_UNSEEN_COUNT.LOAD_FULFILLED':
      return moveToFulfilled(state, action.payload.entities.unseenThreadEdges);
    default:
      return state;
  }
};

export default reducer;
